/* eslint @typescript-eslint/strict-boolean-expressions: off */

import cn from "classnames";
import styles from "gui/pagetitle/pagetitle.module.css";
import Link from "next/link";
import { Avatar } from "gui/avatar/avatar";
import { Cell } from "gui/grid/cell";
import { Grid } from "gui/grid/grid";
import { Badge } from "components/Badge/Badge";
import { IconClockHistory } from "components/Icons/IconClockHistory";
import { Tooltip } from "components/Tooltip/Tooltip";
import { Spacer } from "gui/spacer/spacer";
import React from "react";
import { SelectResourceOwner } from "./SelectResourceOwner/SelectResourceOwner";
import { Owner } from "lib/entries/types";
import { BreadcrumbFolder } from "./BreadcrumbFolder/BreadcrumbFolder";
import { Folder } from "page-components/Library/FolderPage/ThreeDotsMenu/FolderType";

// A page title, typically used on landing pages.
export const PageTitle: React.FC<{
  id?: string;
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  revealOnLoad?: boolean;
}> = (props) => {
  return (
    <>
      <Spacer />
      <Grid
        as="header"
        className={styles.titleLarge}
        revealOnLoad={props.revealOnLoad}
        gaps
      >
        <Cell>
          <h1 id={props.id}>
            {props.children}
            {props.title}
          </h1>
          {props.subtitle && <p>{props.subtitle}</p>}
        </Cell>
      </Grid>
    </>
  );
};

// A page title with a username and avatar prefix.
export const PageTitleOwner: React.FC<{
  owner: Owner;
  eligibleOwners?: Array<Owner>; // additional eligible owners to display in the dropdown
  onOwnerChange?: (owner: Owner) => void;
  id?: string;
  title: string;
  private: boolean;
  unsaved?: boolean;
  isArchived?: boolean;
  tags?: React.ReactNode;
  action?: React.ReactNode;
  caption?: React.ReactNode;
  disableOwnerSelect?: boolean;
  hasMatView?: boolean;
  isPopular?: boolean;
  folder?: {
    contentItem: {
      type: "dashboard" | "query";
      id: number;
    };
    result?: Folder;
    refetchFolder?: () => Promise<void> | void;
  };
}> = (props) => {
  const { owner } = props;

  const avatar = (
    <Link href={`/${owner.handle}`}>
      <a>
        <Avatar src={owner.profile_image_url} size={25} />
      </a>
    </Link>
  );

  const privateLabel = props.private && (
    <span className={styles.private}> / Private</span>
  );

  const unsavedLabel = props.unsaved && (
    <span className={styles.unsaved}> / Unsaved</span>
  );
  const archivedLabel = props.isArchived && <span>[Archived] </span>;

  const popularBadge = props.isPopular && (
    <Tooltip
      position="right-of-center"
      style={{ whiteSpace: "unset", maxWidth: "22rem" }}
      label="Trending dashboards are refreshed periodically by Dune."
    >
      <span className={styles.popularBadge}>
        {" "}
        <Badge variant="filled" size="M" color="brand-orange">
          <IconClockHistory />
          Popular
        </Badge>
      </span>
    </Tooltip>
  );

  const folder = props.folder && (
    <>
      /
      <BreadcrumbFolder
        content={{
          type: props.folder.contentItem.type,
          id: props.folder.contentItem.id,
        }}
        result={props.folder.result}
        refetch={props.folder.refetchFolder}
      />
    </>
  );

  const title = (
    <>
      {archivedLabel}
      {props.title}
      {privateLabel}
      {unsavedLabel}
      {popularBadge}
    </>
  );

  return (
    <header className={styles.titleUser}>
      <div className={styles.avatar}>{avatar}</div>
      <div
        className={cn(
          styles.title,
          props.isArchived ? styles.archived : undefined
        )}
      >
        <div className={styles.titleWrapper}>
          <h1>
            <SelectResourceOwner
              onOwnerChange={props.onOwnerChange}
              disabled={
                !props.eligibleOwners?.length || props.disableOwnerSelect
              }
              owner={props.owner}
              eligibleOwners={props.eligibleOwners}
              hasMatView={props.hasMatView}
              title={props.title}
            />
            {folder}
            <> / {title}</>
          </h1>
          {props.action}
        </div>
        {props.caption && <div className={styles.caption}>{props.caption}</div>}
      </div>
      {props.tags && <div className={styles.tags}>{props.tags}</div>}
      <div className={styles.actions}>{props.children}</div>
    </header>
  );
};
